<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { $t } from '@/composables/translations/useTranslate.ts';

interface Props {
  currentPage: number;
  totalItems: number;
  itemsPerPage?: number;
  maxVisiblePages?: number;
  showPageInfo?: boolean;
  showFirstLast?: boolean;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  itemsPerPage: 12,
  maxVisiblePages: 5,
  showPageInfo: true,
  showFirstLast: true,
  disabled: false,
});

const emit = defineEmits<{
  'update:currentPage': [page: number];
  change: [page: number];
}>();

// Reference for the pagination container to handle keyboard events
const paginationRef = ref<HTMLElement>();

// Computed properties
const totalPages = computed(() => Math.ceil(props.totalItems / props.itemsPerPage));

const isFirstPage = computed(() => props.currentPage === 1);
const isLastPage = computed(() => props.currentPage === totalPages.value);

const startItem = computed(() => {
  if (props.totalItems === 0) return 0;
  return (props.currentPage - 1) * props.itemsPerPage + 1;
});

const endItem = computed(() => {
  const end = props.currentPage * props.itemsPerPage;
  return Math.min(end, props.totalItems);
});

const pageInfo = computed(() => {
  if (props.totalItems === 0) {
    return $t('ibe.pagination.pages-of', '{current} of {total} pages', { current: 0, total: 0 });
  }
  return $t('ibe.pagination.range-of-pages', '{start} - {end} of {total} pages', {
    start: startItem.value,
    end: endItem.value,
    total: props.totalItems,
  });
});

// Calculate visible page numbers
const visiblePages = computed(() => {
  const maxPages = props.maxVisiblePages;

  if (totalPages.value <= maxPages) {
    return Array.from({ length: totalPages.value }, (_, i) => i + 1);
  }

  const half = Math.floor(maxPages / 2);
  let start = Math.max(1, props.currentPage - half);
  const end = Math.min(totalPages.value, start + maxPages - 1);

  // Adjust start if we're near the end
  if (end === totalPages.value) {
    start = Math.max(1, end - maxPages + 1);
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
});

const showLeftEllipsis = computed(() => {
  return totalPages.value > props.maxVisiblePages && visiblePages.value[0] > 1;
});

const showRightEllipsis = computed(() => {
  return (
    totalPages.value > props.maxVisiblePages &&
    visiblePages.value[visiblePages.value.length - 1] < totalPages.value
  );
});

// Navigation methods
const goToPage = (page: number) => {
  if (props.disabled || page < 1 || page > totalPages.value || page === props.currentPage) {
    return;
  }

  emit('update:currentPage', page);
  emit('change', page);
};

const goToPrevious = () => {
  if (!isFirstPage.value) {
    goToPage(props.currentPage - 1);
  }
};

const goToNext = () => {
  if (!isLastPage.value) {
    goToPage(props.currentPage + 1);
  }
};

const goToFirst = () => {
  if (!isFirstPage.value) {
    goToPage(1);
  }
};

const goToLast = () => {
  if (!isLastPage.value) {
    goToPage(totalPages.value);
  }
};

// Keyboard navigation
const handleKeydown = (event: KeyboardEvent) => {
  if (props.disabled) return;

  switch (event.key) {
    case 'ArrowLeft':
      event.preventDefault();
      goToPrevious();
      break;
    case 'ArrowRight':
      event.preventDefault();
      goToNext();
      break;
    case 'Home':
      event.preventDefault();
      goToFirst();
      break;
    case 'End':
      event.preventDefault();
      goToLast();
      break;
    case '1':
    case '2':
    case '3':
    case '4':
    case '5':
    case '6':
    case '7':
    case '8':
    case '9': {
      // Direct page access with number keys (1-9)
      event.preventDefault();
      const pageNumber = parseInt(event.key);
      if (pageNumber <= totalPages.value) {
        goToPage(pageNumber);
      }
      break;
    }
  }
};

onMounted(() => {
  if (paginationRef.value) {
    paginationRef.value.addEventListener('keydown', handleKeydown);
  }
});

onUnmounted(() => {
  if (paginationRef.value) {
    paginationRef.value.removeEventListener('keydown', handleKeydown);
  }
});

// Button classes
const baseButtonClasses =
  'w-12 h-12 flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-actions-primary focus:ring-offset-2';
const inactiveButtonClasses = 'bg-actions-tertiary text-secondary hover:bg-actions-tertiary-hover';
const activeButtonClasses =
  'bg-transparent border-2 border-actions-primary text-secondary font-semibold';
const disabledButtonClasses = 'opacity-40 cursor-not-allowed';
</script>

<template>
  <nav
    ref="paginationRef"
    :aria-label="$t('ibe.pagination.label', 'Pagination')"
    class="flex flex-col items-center w-full gap-4"
    tabindex="0"
  >
    <!-- Pagination Controls -->
    <div v-if="totalPages > 1" class="flex items-center gap-8 mdDown:gap-4">
      <!-- Previous/First Section -->
      <div class="flex items-center gap-2.5 mdDown:gap-1">
        <button
          v-if="showFirstLast"
          type="button"
          :disabled="disabled || isFirstPage"
          :aria-label="$t('ibe.pagination.first', 'First page')"
          :class="[
            baseButtonClasses,
            inactiveButtonClasses,
            { [disabledButtonClasses]: disabled || isFirstPage },
          ]"
          class="mdDown:hidden"
          @click="goToFirst"
        >
          <span class="icon icon-pagination-first w-4 h-3 bg-current" :aria-hidden="true"></span>
        </button>
        <button
          type="button"
          :disabled="disabled || isFirstPage"
          :aria-label="$t('ibe.common.previous', 'Previous')"
          :class="[
            baseButtonClasses,
            inactiveButtonClasses,
            { [disabledButtonClasses]: disabled || isFirstPage },
          ]"
          @click="goToPrevious"
        >
          <span class="icon icon-pagination-prev w-4 h-3 bg-current" :aria-hidden="true"></span>
        </button>
      </div>

      <!-- Page Numbers Section - Hidden on mobile, reduced on tablet -->
      <div class="flex items-center gap-2.5 mdDown:hidden lg:flex">
        <span v-if="showLeftEllipsis" class="text-secondary text-body px-2"> ... </span>

        <button
          v-for="page in visiblePages"
          :key="page"
          type="button"
          :disabled="disabled"
          :aria-label="$t('ibe.pagination.go-to-page', 'Go to page {page}', { page })"
          :aria-current="page === currentPage ? 'page' : undefined"
          :class="[
            baseButtonClasses,
            page === currentPage ? activeButtonClasses : inactiveButtonClasses,
            { [disabledButtonClasses]: disabled },
          ]"
          class="text-body"
          @click="goToPage(page)"
        >
          {{ page }}
        </button>

        <span v-if="showRightEllipsis" class="text-secondary text-body px-2"> ... </span>
      </div>

      <!-- Mobile Current Page Indicator -->
      <div class="hidden mdDown:flex items-center">
        <span class="text-secondary text-body px-4"> {{ currentPage }} / {{ totalPages }} </span>
      </div>

      <!-- Next/Last Section -->
      <div class="flex items-center gap-2.5 mdDown:gap-1">
        <button
          type="button"
          :disabled="disabled || isLastPage"
          :aria-label="$t('ibe.common.next', 'Next')"
          :class="[
            baseButtonClasses,
            inactiveButtonClasses,
            { [disabledButtonClasses]: disabled || isLastPage },
          ]"
          @click="goToNext"
        >
          <span class="icon icon-pagination-next w-4 h-3 bg-current" :aria-hidden="true"></span>
        </button>

        <button
          v-if="showFirstLast"
          type="button"
          :disabled="disabled || isLastPage"
          :aria-label="$t('ibe.pagination.last', 'Last page')"
          :class="[
            baseButtonClasses,
            inactiveButtonClasses,
            { [disabledButtonClasses]: disabled || isLastPage },
          ]"
          class="mdDown:hidden"
          @click="goToLast"
        >
          <span class="icon icon-pagination-last w-4 h-3 bg-current" :aria-hidden="true"></span>
        </button>
      </div>
    </div>

    <!-- Page Info Section - Now Below Controls -->
    <div v-if="showPageInfo" class="text-gray-600 text-body-small text-center">
      {{ pageInfo }}
    </div>
  </nav>
</template>
